import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '../../services/loanService';
import { formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const TYPE_ICONS = {
  kyc_update: '🪪', loan_status: '⬡', offer_received: '💌',
  payment_due: '⏰', payment_received: '✓', disbursement: '₦',
  document_request: '📄', system: 'ℹ',
};

const TYPE_COLORS = {
  kyc_update: '#5B8EFF', loan_status: '#818CF8', offer_received: '#00C896',
  payment_due: '#FF4757', payment_received: '#00C896', disbursement: '#F5A623',
  document_request: '#F5A623', system: '#94a3b8',
};

const TYPE_LABELS = {
  kyc_update: 'KYC Update', loan_status: 'Loan Status', offer_received: 'Offer Received',
  payment_due: 'Payment Due', payment_received: 'Payment Received', disbursement: 'Disbursement',
  document_request: 'Document Request', system: 'System',
};

function NotifDetailModal({ notif, onClose }) {
  const meta = notif.metadata || {};
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div className="card" style={{ width: 480, maxHeight: '85vh', overflow: 'auto' }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
              background: `${TYPE_COLORS[notif.notif_type] || '#94a3b8'}18`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem',
            }}>
              {TYPE_ICONS[notif.notif_type] || 'ℹ'}
            </div>
            <div>
              <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: TYPE_COLORS[notif.notif_type] || 'var(--text-3)', marginBottom: 2 }}>
                {TYPE_LABELS[notif.notif_type] || notif.notif_type}
              </div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-1)', lineHeight: 1.3 }}>{notif.title}</div>
            </div>
          </div>
          <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem', flexShrink: 0 }} onClick={onClose}>✕</button>
        </div>

        {/* Message */}
        <div style={{ background: 'var(--surface-alt)', borderRadius: 'var(--radius)', padding: '1rem', marginBottom: '1rem' }}>
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-2)', lineHeight: 1.65 }}>{notif.message}</p>
        </div>

        {/* Metadata */}
        {meta.application_number && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>
              Related Application
            </div>
            <div style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)', fontSize: '0.9rem' }}>
              {meta.application_number}
            </div>
          </div>
        )}
        {Array.isArray(meta.documents) && meta.documents.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>
              Documents Required
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {meta.documents.map(d => (
                <span key={d} style={{
                  fontSize: '0.75rem', fontWeight: 600, padding: '0.2rem 0.6rem',
                  borderRadius: 99, background: 'rgba(245,166,35,0.12)',
                  color: '#F5A623', border: '1px solid rgba(245,166,35,0.25)',
                }}>
                  {d.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.75rem', borderTop: '1px solid var(--border)' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{formatDate(notif.created_at)}</span>
          <button className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '0.4rem 1rem' }} onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Notifications() {
  const qc = useQueryClient();
  const [openNotif, setOpenNotif] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationService.getAll({ page_size: 50 }).then(r => r.data),
  });

  const markReadMutation = useMutation({
    mutationFn: (id) => notificationService.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notif-unread-count'] });
    },
  });

  const markAllMutation = useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notif-unread-count'] });
    },
  });

  const handleOpen = (n) => {
    if (!n.is_read) markReadMutation.mutate(n.id);
    setOpenNotif(n);
  };

  const notifs = data?.data?.results || data?.data || [];
  const unreadCount = data?.unread_count || 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Notifications</h1>
          <p>{unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}</p>
        </div>
        {unreadCount > 0 && (
          <button className="btn btn-ghost" style={{ fontSize: '0.8rem' }} disabled={markAllMutation.isPending} onClick={() => markAllMutation.mutate()}>
            Mark all read
          </button>
        )}
      </div>

      {isLoading ? <LoadingSpinner /> : (
        <div className="card" style={{ padding: 0 }}>
          {notifs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🔔</div>
              <p>No notifications yet.</p>
            </div>
          ) : notifs.map(n => {
            const color = TYPE_COLORS[n.notif_type] || '#94a3b8';
            return (
              <div
                key={n.id}
                onClick={() => handleOpen(n)}
                style={{
                  display: 'flex', gap: '1rem', padding: '1rem 1.25rem',
                  borderBottom: '1px solid var(--border)',
                  background: n.is_read ? 'transparent' : 'rgba(91,142,255,0.04)',
                  cursor: 'pointer',
                  transition: 'background .15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = n.is_read ? 'var(--surface-alt)' : 'rgba(91,142,255,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = n.is_read ? 'transparent' : 'rgba(91,142,255,0.04)'; }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: `${color}18`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1rem',
                }}>
                  {TYPE_ICONS[n.notif_type] || 'ℹ'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <div style={{ fontWeight: n.is_read ? 400 : 600, fontSize: '0.875rem', color: 'var(--text-1)' }}>{n.title}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', flexShrink: 0 }}>{formatDate(n.created_at)}</div>
                  </div>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-2)', marginTop: '0.25rem', lineHeight: 1.5,
                    overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {n.message}
                  </p>
                  <span style={{ fontSize: '0.7rem', color: color, fontWeight: 600, marginTop: '0.2rem', display: 'inline-block' }}>
                    Tap to read more →
                  </span>
                </div>
                {!n.is_read && (
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#5B8EFF', flexShrink: 0, marginTop: 6 }} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {openNotif && (
        <NotifDetailModal notif={openNotif} onClose={() => setOpenNotif(null)} />
      )}
    </div>
  );
}
